import { type FC, useCallback } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { trackEvent } from 'dibs-tracking';
import { SeoLink } from 'dibs-seo/exports/SeoLink.tsx';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea.tsx';
import { type PdpRespStorytellingText_storyTellingTab$data } from './__generated__/PdpRespStorytellingText_storyTellingTab.graphql.ts';
import { fireReadMoreTracking } from '../PdpSharedStorytellingComponents/pdpSharedStorytellingHelpers.ts';
import styles from '../PdpSharedStorytellingComponents/PdpSharedStorytellingText.scss';
import { moreDetails, seeLess } from '../pdpMessages.tsx';

type Props = {
    storyTellingTab: PdpRespStorytellingText_storyTellingTab$data;
    showExpandingArea?: boolean;
    showHeader?: boolean;
};

const PdpRespStorytellingTextComponent: FC<Props> = ({
    storyTellingTab,
    showExpandingArea = true,
    showHeader = true,
}) => {
    const { tabTitle, contentHeader, contentCreatorAttribution, contentBodyText, tabType } =
        storyTellingTab;

    const onReadMoreClick = useCallback(() => {
        fireReadMoreTracking({ tabType });
    }, [tabType]);

    const content = (
        <div
            className={styles.contentBodyText}
            dangerouslySetInnerHTML={{ __html: contentBodyText }}
        />
    );

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    {showHeader && (
                        <Heading dataTn="pdp-storytelling-about-info" className={styles.tabTitle}>
                            {tabTitle}
                        </Heading>
                    )}
                    {contentHeader && (
                        <div className={styles.contentHeaderWrapper}>
                            <Heading
                                dataTn="pdp-storytelling-creator-name"
                                className={styles.contentHeader}
                                dangerouslySetInnerHTML={{ __html: contentHeader }}
                            />
                        </div>
                    )}

                    {contentCreatorAttribution && (
                        <div className={styles.creatorAttribution}>
                            <FormattedMessage
                                defaultMessage="Designed by {creator}"
                                id="pdp.resp.storytelling.creatorAttribution"
                                values={{
                                    creator: (
                                        <SeoLink
                                            linkData={contentCreatorAttribution.creatorBrowseUrl}
                                            dataTn="pdp-storytelling-design-attribution-link"
                                            onClick={() => {
                                                trackEvent({
                                                    category: 'promo interaction',
                                                    action: 'iconic design creator clicked',
                                                    label: 'pdp',
                                                });
                                            }}
                                        >
                                            {contentCreatorAttribution?.creatorName}
                                        </SeoLink>
                                    ),
                                }}
                            />
                        </div>
                    )}
                    {showExpandingArea ? (
                        <ExpandingAreaWithReadMore
                            // TODO: Uncomment lineClamp prop once issue with Safari is resolved
                            // lineClamp={5}
                            showGradient={false}
                            collapsedHeight={120}
                            readMore={moreDetails}
                            readLess={seeLess}
                            onReadMoreClick={onReadMoreClick}
                            wrapperClass={styles.expandingAreaWrapper}
                        >
                            {content}
                        </ExpandingAreaWithReadMore>
                    ) : (
                        content
                    )}
                </>
            )}
        </HeadingLevel>
    );
};

export const PdpRespStorytellingText = createFragmentContainer(PdpRespStorytellingTextComponent, {
    storyTellingTab: graphql`
        fragment PdpRespStorytellingText_storyTellingTab on StoryTellingTab {
            tabTitle
            contentHeader
            contentCreatorAttribution {
                creatorName
                creatorBrowseUrl {
                    ...SeoLink_linkData
                }
            }
            contentBodyText

            tabType
        }
    `,
});
